import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sun, Moon, LogOut, Tag, ArrowDownCircle, ArrowUpCircle, User, Mail, ShieldCheck, Building2, Save, PenLine, Upload } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { api, cx, API, uploadFile } from "@/lib/api";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";

export default function Impostazioni() {
  const { theme, toggle } = useTheme();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState(null);
  const [company, setCompany] = useState({ partita_iva: "", ragione_sociale: "", indirizzo: "", cap: "", citta: "", provincia: "", telefono: "", email: "", pec: "", sdi: "", firma_file_id: "" });
  const [savingCompany, setSavingCompany] = useState(false);
  const [uploadingFirma, setUploadingFirma] = useState(false);
  const isOwner = user?.role === "owner";

  useEffect(() => { api.get("/categories").then(setCategories); }, []);
  useEffect(() => {
    api.get("/company/settings").then((c) =>
      setCompany({
        partita_iva: c?.partita_iva || "", ragione_sociale: c?.ragione_sociale || "",
        indirizzo: c?.indirizzo || "", cap: c?.cap || "", citta: c?.citta || "", provincia: c?.provincia || "",
        telefono: c?.telefono || "", email: c?.email || "", pec: c?.pec || "", sdi: c?.sdi || "", firma_file_id: c?.firma_file_id || "",
      })
    );
  }, []);

  const saveCompany = async () => {
    setSavingCompany(true);
    try {
      await api.put("/company/settings", company);
      toast.success("Dati azienda salvati.");
    } catch (e) {
      toast.error(e.message || "Salvataggio non riuscito.");
    } finally {
      setSavingCompany(false);
    }
  };

  const handleLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Impostazioni</h1>
        <p className="text-sm text-muted-foreground mt-1">Gestisci account, aspetto e categorie.</p>
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Profilo</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-3 text-sm"><User className="h-4 w-4 text-muted-foreground" /><span className="text-muted-foreground w-24">Nome</span><span className="font-medium">{user?.name || "—"}</span></div>
          <div className="flex items-center gap-3 text-sm"><Mail className="h-4 w-4 text-muted-foreground" /><span className="text-muted-foreground w-24">Email</span><span className="font-medium">{user?.email}</span></div>
          <div className="flex items-center gap-3 text-sm"><ShieldCheck className="h-4 w-4 text-muted-foreground" /><span className="text-muted-foreground w-24">Accesso</span>
            <span className="font-medium capitalize">{user?.auth_provider === "google" ? "Google" : "Email e Password"}{user?.role === "owner" ? " · Titolare" : ""}</span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Aspetto</CardTitle></CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {theme === "dark" ? <Moon className="h-5 w-5 text-amber-500" /> : <Sun className="h-5 w-5 text-amber-500" />}
              <div>
                <p className="text-sm font-medium">Tema {theme === "dark" ? "Scuro" : "Chiaro"}</p>
                <p className="text-xs text-muted-foreground">Passa tra modalità chiara e scura.</p>
              </div>
            </div>
            <Switch checked={theme === "dark"} onCheckedChange={toggle} data-testid="settings-theme-switch" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><Building2 className="h-4 w-4" /> Dati Azienda</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-muted-foreground">
            La Partita IVA serve per smistare automaticamente le fatture importate (XML) tra emesse (entrate) e ricevute (uscite).
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="company-piva">Partita IVA</Label>
              <Input
                id="company-piva" data-testid="company-piva-input"
                value={company.partita_iva}
                onChange={(e) => setCompany((c) => ({ ...c, partita_iva: e.target.value }))}
                placeholder="es. 01234567890" disabled={!isOwner}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="company-nome">Ragione sociale</Label>
              <Input
                id="company-nome" data-testid="company-nome-input"
                value={company.ragione_sociale}
                onChange={(e) => setCompany((c) => ({ ...c, ragione_sociale: e.target.value }))}
                placeholder="es. Ellevi Costruzioni S.r.l." disabled={!isOwner}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Indirizzo</Label><Input data-testid="company-indirizzo-input" value={company.indirizzo} onChange={(e) => setCompany((c) => ({ ...c, indirizzo: e.target.value }))} disabled={!isOwner} /></div>
            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-1.5"><Label>CAP</Label><Input data-testid="company-cap-input" value={company.cap} onChange={(e) => setCompany((c) => ({ ...c, cap: e.target.value }))} disabled={!isOwner} /></div>
              <div className="space-y-1.5"><Label>Città</Label><Input data-testid="company-citta-input" value={company.citta} onChange={(e) => setCompany((c) => ({ ...c, citta: e.target.value }))} disabled={!isOwner} /></div>
              <div className="space-y-1.5"><Label>Prov.</Label><Input data-testid="company-provincia-input" value={company.provincia} onChange={(e) => setCompany((c) => ({ ...c, provincia: e.target.value }))} disabled={!isOwner} /></div>
            </div>
            <div className="space-y-1.5"><Label>Telefono</Label><Input data-testid="company-telefono-input" value={company.telefono} onChange={(e) => setCompany((c) => ({ ...c, telefono: e.target.value }))} disabled={!isOwner} /></div>
            <div className="space-y-1.5"><Label>Email</Label><Input data-testid="company-email-input" value={company.email} onChange={(e) => setCompany((c) => ({ ...c, email: e.target.value }))} disabled={!isOwner} /></div>
            <div className="space-y-1.5"><Label>PEC</Label><Input data-testid="company-pec-input" value={company.pec} onChange={(e) => setCompany((c) => ({ ...c, pec: e.target.value }))} disabled={!isOwner} /></div>
            <div className="space-y-1.5"><Label>Codice SDI</Label><Input data-testid="company-sdi-input" value={company.sdi} onChange={(e) => setCompany((c) => ({ ...c, sdi: e.target.value }))} disabled={!isOwner} /></div>
          </div>
          <div className="space-y-2 pt-3 border-t">
            <Label className="flex items-center gap-2"><PenLine className="h-4 w-4" /> Firma società (per PDF ODA/CEL firmati)</Label>
            {company.firma_file_id ? (
              <div className="flex items-center gap-3">
                <img src={`${API}/uploads/${company.firma_file_id}`} alt="firma società" className="h-16 max-w-[220px] object-contain rounded border bg-white p-1" data-testid="company-firma-preview" />
                {isOwner && <Button type="button" variant="ghost" size="sm" className="text-destructive" data-testid="company-firma-remove" onClick={() => setCompany((c) => ({ ...c, firma_file_id: "" }))}>Rimuovi</Button>}
              </div>
            ) : isOwner ? (
              <label className="flex items-center gap-2 cursor-pointer text-sm rounded-md border border-dashed px-3 py-2 hover:bg-secondary w-fit" data-testid="company-firma-upload">
                <Upload className="h-4 w-4" /> {uploadingFirma ? "Caricamento..." : "Carica firma"}
                <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" disabled={uploadingFirma}
                  onChange={async (e) => {
                    const file = e.target.files?.[0]; if (!file) return;
                    setUploadingFirma(true);
                    try { const res = await uploadFile(file); setCompany((c) => ({ ...c, firma_file_id: res.file_id })); toast.success("Firma caricata. Premi Salva per confermare."); }
                    catch (err) { toast.error(err.message || "Caricamento non riuscito."); }
                    finally { setUploadingFirma(false); e.target.value = ""; }
                  }} />
              </label>
            ) : <p className="text-sm text-muted-foreground">Nessuna firma caricata.</p>}
            {isOwner && <p className="text-xs text-muted-foreground">Ricordati di premere "Salva dati azienda" dopo aver caricato o rimosso la firma.</p>}
          </div>
          {isOwner ? (
            <Button onClick={saveCompany} disabled={savingCompany} data-testid="company-save-button" className="gap-2">
              <Save className="h-4 w-4" /> Salva dati azienda
            </Button>
          ) : (
            <p className="text-xs text-muted-foreground">Solo il titolare può modificare i dati azienda.</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><Tag className="h-4 w-4" /> Categorie Predefinite</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <p className="flex items-center gap-1.5 text-sm font-medium text-emerald-600 mb-2"><ArrowUpCircle className="h-4 w-4" /> Entrate</p>
            <ul className="space-y-1.5">
              {categories?.entrate.map((c) => (
                <li key={c.key} className="text-sm text-muted-foreground flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />{c.label}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="flex items-center gap-1.5 text-sm font-medium text-red-600 mb-2"><ArrowDownCircle className="h-4 w-4" /> Uscite</p>
            <ul className="space-y-1.5">
              {categories?.uscite.map((c) => (
                <li key={c.key} className="text-sm text-muted-foreground flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />{c.label}
                </li>
              ))}
            </ul>
          </div>
        </CardContent>
      </Card>

      <Card className="border-destructive/30">
        <CardContent className="flex items-center justify-between p-5">
          <div>
            <p className="text-sm font-medium">Esci dall'account</p>
            <p className="text-xs text-muted-foreground">Termina la sessione corrente.</p>
          </div>
          <Button variant="outline" onClick={handleLogout} data-testid="settings-logout-button" className="gap-2 text-destructive border-destructive/40 hover:bg-destructive/10">
            <LogOut className="h-4 w-4" /> Esci
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
